import React, { useState } from "react";
import { Box, Button, Heading, Layer, Text } from "grommet";
import {
  formatMoney,
  getMenuItemTotalPrice,
  getMenuSubtotal,
  roundPrice,
} from "@common/utils";
import { ChargeService } from "@services";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastErrorContent } from "@shared/Toast";
import { useTranslation } from "react-i18next";

export default function ViewMenuRequestModal(props) {
  const { t } = useTranslation();
  const { onDecline, onApprove, chargeId, status, hotel } = props;
  const [open, setOpen] = useState(false);
  const [charge, setCharge] = useState();
  const onClose = () => setOpen(false);

  const onOpen = () => {
    presentLoading();
    ChargeService.get(chargeId).then(
      (response) => {
        dismissLoading();
        setCharge(response.data);
        setOpen(true);
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  const handleApprove = (approved) => {
    onClose();
    onApprove();
  };

  const handleDecline = (approved) => {
    onClose();
    onDecline();
  };

  const getSubTotalCart = () => {
    return getMenuSubtotal(charge?.items);
  };

  const getTaxes = () => {
    if (charge.category)
      return roundPrice(
        (getSubTotalCart() * (charge.category.taxPercent || 0)) / 100,
      );
    return 0;
  };

  const getTotalCart = () => {
    return roundPrice(
      getSubTotalCart() + Number(charge.category?.fee || 0) + getTaxes(),
    );
  };

  return (
    <div>
      {status === "requested" ? (
        <Button
          label={t("menuRequest.reviewRequest")}
          onClick={onOpen}
          primary
          color="status-warning"
          alignSelf="start"
        />
      ) : (
        <Button
          label={t("common.view")}
          onClick={onOpen}
          primary
          color="brand"
          alignSelf="start"
        />
      )}

      {open && charge && (
        <Layer
          position="center"
          onClickOutside={onClose}
          onEsc={onClose}
          className="view-menu-request-modal"
        >
          <Box
            pad="medium"
            gap="small"
            width="large"
            height="stretch"
            alignSelf="start"
          >
            <Heading level={3} margin="none">
              {charge.name}
            </Heading>

            {charge.items && charge.items.length > 0 && (
              <Box align="center" gap="medium">
                {charge.items.map((item) => (
                  <Box
                    key={item.id}
                    className="cartItem"
                    justify="between"
                    direction="row"
                    gap="small"
                  >
                    <Box direction="row" gap="small">
                      <Text className="quantity">{item.quantity}</Text>
                      <Box>
                        <Text className="productName">{item.name}</Text>
                        {item.options.length > 0 && (
                          <Text italic size={13} className="options">
                            {item.options
                              .map((x) => x.list.map((y) => y.name).join(", "))
                              .join(", ")}
                          </Text>
                        )}
                      </Box>
                    </Box>
                    <Text>
                      {formatMoney(getMenuItemTotalPrice(item), hotel.currency)}
                    </Text>
                  </Box>
                ))}
                <Box className="totals" gap="medium">
                  <Box direction="row" justify="between">
                    <Text>{t("menuRequest.subtotal")}</Text>
                    <Text>
                      {formatMoney(getSubTotalCart(), hotel.currency)}
                    </Text>
                  </Box>
                  {!!charge.category &&
                    !!charge.category.fee &&
                    !!charge.category.feeLabel && (
                      <Box direction="row" justify="between">
                        <Text>{charge.category.feeLabel}</Text>
                        <Text>
                          {formatMoney(charge.category.fee, hotel.currency)}
                        </Text>
                      </Box>
                    )}
                  <Box direction="row" justify="between">
                    <Text>{t("menuRequest.taxes")}</Text>
                    <Text>{formatMoney(getTaxes(), hotel.currency)}</Text>
                  </Box>
                  <Box className="total" direction="row" justify="between">
                    <Text bold>{t("menuRequest.total")}</Text>
                    <Text>{formatMoney(getTotalCart(), hotel.currency)}</Text>
                  </Box>
                </Box>
              </Box>
            )}

            {charge.status === "requested" ? (
              <Box direction="row" justify="between">
                <Button
                  label={t("common.decline")}
                  onClick={() => handleDecline()}
                  primary
                  color="status-error"
                />
                <Button
                  label={t("common.approve")}
                  onClick={() => handleApprove()}
                  primary
                  color="status-ok"
                />
              </Box>
            ) : (
              <Button
                label={t("common.close")}
                onClick={onClose}
                color="dark-3"
              />
            )}
          </Box>
        </Layer>
      )}
    </div>
  );
}

ViewMenuRequestModal.propTypes = {};
ViewMenuRequestModal.defaultProps = {};
